import { Icon } from '@design-system/components/atoms'
import { mediaQuery, typo } from '@design-system/styles'
import { css } from '@emotion/react'
import clsx from 'clsx'
import { observer } from 'mobx-react-lite'
import React, { useState } from 'react'
import { Scrollbars } from 'react-custom-scrollbars-2'
import { type NavLink, useLocation } from 'react-router-dom'

import type { Badge } from '@/component/atoms/Badge/Badge'
import {
  NestedSideNavigationLink,
  SideNavigationLink,
} from '@/component/atoms/NavigationLink/SideNavigationLink'
import PageTemplateService from '@/component/templates/PageTemplate/_service/PageTemplateService'
import useRepository from '@/hooks/useRepository'

type NavItem = {
  label: string
  labelContent?: React.ReactNode
  to: React.ComponentProps<NavLink>['to']
  matchUrl?: string | string[]
  restrictionMessage?: string
  isNew?: boolean
  hasShareIcon?: boolean
  badgeProps?: React.ComponentPropsWithRef<typeof Badge>
  badgePosition?: 'left' | 'center' | 'right'
  children?: (Omit<NavItem, 'badgeProps' | 'badgePosition'> & { urlPatterns?: string[] })[]
}
type NavInfo = NavItem | false | undefined

export type NavInfoList = {
  title?: string
  items: NavInfo[]
}[]

type Props = {
  navInfoList: NavInfoList
  foldable?: boolean
  isFixed?: boolean
}

type NavChild = NonNullable<NavItem['children']>[number]

/** 로딩 스켈레톤 폭도 이 값을 써야 마운트 시 수평 점프가 없다 */
export const SIDE_NAVIGATION_WIDTH = 198

function resolveSideNavItemKey(item: NavItem, itemIndex: number): string {
  return [item.label, item.matchUrl ?? '', itemIndex].join('::')
}

function resolveSideNavChildKey(child: NavChild, childIndex: number): string {
  return [child.label, child.urlPatterns?.join('|') ?? '', childIndex].join('::')
}

function SideNavigation({ navInfoList, foldable = false, isFixed = false }: Props) {
  const service = useRepository(PageTemplateService)

  const { pathname } = useLocation()

  const [collapsedGroupTitles, setCollapsedGroupTitles] = useState<ReadonlySet<string>>(
    () => new Set(),
  )

  const toggleGroup = (groupTitle: string) => {
    setCollapsedGroupTitles((previous) => {
      const next = new Set(previous)
      if (next.has(groupTitle)) {
        next.delete(groupTitle)
      } else {
        next.add(groupTitle)
      }
      return next
    })
  }

  const sections = navInfoList
    .map((group) => ({
      ...group,
      items: group.items.filter((item): item is NavItem => Boolean(item)),
    }))
    .filter((group) => group.items.length > 0)

  return (
    <div
      css={S.container}
      className={clsx(
        service.isSideNavigationOpen && 'open',
        foldable && 'foldable',
        isFixed && 'fixed',
      )}
    >
      <nav>
        <Scrollbars autoHide autoHideDuration={1000} style={{ height: '100%' }}>
          <div className="side-nav-scroll-inner">
            {sections.map((navInfo, index) => {
              const isCollapsed = navInfo.title ? collapsedGroupTitles.has(navInfo.title) : false
              // divider 는 아코디언(title 있는 섹션) 위·아래에만 — title 없는 1뎁스 그룹엔 붙이지 않는다
              const hasDividerAfter = !!navInfo.title || !!sections[index + 1]?.title

              return (
                <React.Fragment key={index}>
                  {navInfo.title && (
                    <button
                      type="button"
                      className="side-nav-title"
                      aria-expanded={!isCollapsed}
                      onClick={() => toggleGroup(navInfo.title!)}
                    >
                      <span>{navInfo.title}</span>
                      <span className="side-nav-title-chevron">
                        <Icon name="chevron_up" size={16} color="var(--Colors-Gray-700)" />
                      </span>
                    </button>
                  )}
                  <div
                    className={clsx('side-nav-group-collapsible', navInfo.title && 'collapsible')}
                    data-collapsed={isCollapsed}
                  >
                    <div className="side-nav-group">
                      {navInfo.items.map((item, itemIndex) => {
                        if (
                          item.children &&
                          typeof item.matchUrl === 'string' &&
                          pathname.includes(item.matchUrl)
                        ) {
                          return (
                            <div
                              className="side-nav-item-nested"
                              key={resolveSideNavItemKey(item, itemIndex)}
                            >
                              {item.labelContent ?? item.label}
                              {item.children.map((child, childIndex) => (
                                <NestedSideNavigationLink
                                  key={resolveSideNavChildKey(child, childIndex)}
                                  {...child}
                                >
                                  {child.labelContent ?? child.label}
                                </NestedSideNavigationLink>
                              ))}
                            </div>
                          )
                        }

                        return (
                          <React.Fragment key={resolveSideNavItemKey(item, itemIndex)}>
                            <SideNavigationLink
                              variant={navInfo.title ? 'section' : 'top'}
                              {...item}
                            >
                              {item.labelContent ?? item.label}
                            </SideNavigationLink>
                          </React.Fragment>
                        )
                      })}
                    </div>
                  </div>
                  {hasDividerAfter && <div className="side-nav-divider" />}
                </React.Fragment>
              )
            })}
          </div>
        </Scrollbars>
      </nav>
    </div>
  )
}

export default observer(SideNavigation)

const S = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 0 0 ${SIDE_NAVIGATION_WIDTH}px;
    height: 100%;
    padding-top: 8px;
    background: var(--Colors-Gray-100);
    border-right: 1px solid var(--Colors-Gray-400);

    nav {
      width: 100%;
      height: 0;
      flex: 1 1 auto;
    }

    .side-nav-scroll-inner {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 0 16px;
    }

    .side-nav-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-shrink: 0;
      width: 100%;
      padding: 12px 12px 0;
      background: none;
      border: none;
      cursor: pointer;
      ${typo.caption01};
      color: var(--Colors-Sky-400);
      font-weight: 700;
    }

    .side-nav-title-chevron {
      display: inline-flex;
      transition: transform 0.24s cubic-bezier(0.22, 1, 0.36, 1);
    }

    .side-nav-title[aria-expanded='false'] .side-nav-title-chevron {
      transform: rotate(180deg);
    }

    @media (prefers-reduced-motion: reduce) {
      .side-nav-title-chevron,
      .side-nav-group-collapsible.collapsible {
        transition: none;
      }
    }

    .side-nav-group-collapsible {
      display: grid;
      grid-template-rows: 1fr;
      flex-shrink: 0;

      &.collapsible {
        transition: grid-template-rows 0.26s cubic-bezier(0.22, 1, 0.36, 1);
      }

      &[data-collapsed='true'] {
        grid-template-rows: 0fr;
      }
    }

    .side-nav-group {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-height: 0;
      overflow: hidden;

      > * {
        flex-shrink: 0;
      }
    }

    .side-nav-item-nested {
      display: flex;
      flex-direction: column;
      gap: 10px;
      border-radius: var(--Radius-200);
      background: var(--Colors-Sky-50);
      padding: 14px 12px;
      ${typo.body02};
      color: var(--Colors-Sky-400);
      font-weight: 700;
    }

    .side-nav-divider {
      flex-shrink: 0;
      height: 1px;
      background: var(--Colors-Gray-300);
    }

    &.foldable {
      transition: all 0.3s ease-in-out;
      overflow: hidden;

      ${mediaQuery.large} {
        position: absolute;
        width: 0;
        height: 100%;
        flex: 0 0 0px;
        padding: 8px 0 0;
        visibility: hidden;
        z-index: 10;
        transform: translateX(-100%);

        &.fixed {
          position: fixed;
          top: 76px;
          left: 0;
          max-height: calc(100vh - 92px);
        }

        nav {
          width: ${SIDE_NAVIGATION_WIDTH}px;
        }

        &.open {
          flex: 0 0 ${SIDE_NAVIGATION_WIDTH}px;
          width: ${SIDE_NAVIGATION_WIDTH}px;
          transform: translateX(0);
          visibility: visible;
        }
      }
    }
  `,
}
